<template>
  <modal
    :show="isModalVisible"
    :class="modalColor()"
    modal-classes="modal-xl"
    @close="hide"
  >
    <h4 slot="header" class="modal-title">
      {{ $t('modals.search_invoice_modal.title') }}
    </h4>

    <div class="row">
      <div class="col text-center py-4">
        <div v-if="isLoading" class="text-center">
          <div class="custom-spinner"></div>
          <p class="mt-2">{{ $t('modals.search_invoice_modal.loading') }}</p>
        </div>

        <div v-else>
          <template v-if="nfData && nfData.found">
            <div class="row d-flex">
              <div class="col-md d-flex flex-column">
                <p>{{ $t('modals.search_invoice_modal.request') }}</p>
                <h4>{{ nfData.request_id ? nfData.request_id : $t('modals.search_invoice_modal.not_present') }}</h4>
              </div>
              <div class="col-md d-flex flex-column">
                <p>{{ $t('modals.search_invoice_modal.order') }}</p>
                <h4 v-if="nfData.order_id">
                  <a href="#" @click.prevent="redirectToOrder(nfData.order_id)">
                    {{ nfData.shipper_order_code }}
                  </a>
                </h4>
                <h4 v-else>
                  {{ $t('modals.search_invoice_modal.not_present') }}
                </h4>
              </div>
            </div>
            <div class="row d-flex">
              <div class="col-md d-flex flex-column">
                <p>{{ $t('modals.search_invoice_modal.delivery_date') }}</p>
                <h4>
                  {{ formatDate(nfData.delivery_date_time) }}
                </h4>
              </div>
              <div class="col-md d-flex flex-column">
                <p>{{ $t('modals.search_invoice_modal.scheduling_date') }}</p>
                <h4>
                  {{ formatDate(nfData.scheduling_date_time) }}
                </h4>
              </div>
            </div>
            <div class="row d-flex mt-4" v-if="nfData.is_delayed">
              <div class="col-md d-flex flex-column align-items-center">
                <i class="tim-icons icon-alert-circle-exc text-danger" />
                <h2 class="text-danger">{{ $t('modals.search_invoice_modal.delayed') }}</h2>
              </div>
            </div>
            <div class="row">
              <div class="col-md-12">
                <div style="display:inline-block;width:100%;overflow-y:auto;">
                  <ul class="timeline timeline-horizontal">
                    <li class="timeline-item" v-for="status in statusItems" :key="status.id">
                      <div :class="'timeline-badge badge-status-' + status.color"><i :class='status.icon'></i></div>
                      <div class="timeline-panel">
                        <div class="timeline-heading">
                          <div class="text-center">
                            <div class="row">
                              <div class="ml-auto mr-auto">
                                <span :class="'badge badge-pill badge-status-' + status.color">
                                  {{ $t(`timeline.${status.label}.header`) }}
                                </span>
                              </div>
                            </div>
                            <div class="row">
                              <div class="ml-auto mr-auto">
                                <span
                                  class="text-muted ml-2"
                                  v-if="status.timestamp !== null"
                                >
                                  {{ status.timestamp | moment("DD/MM/Y HH:mm") }}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </template>
          <template v-else>
            <h1>{{ $t('modals.search_invoice_modal.not_found') }}</h1>
          </template>
        </div>
      </div>
    </div>

    <template slot="footer">
      <base-button type="primary" class="mb-3 ml-auto mr-auto" @click="hide">
        {{ $t('placeholders.close') }}
      </base-button>
    </template>
  </modal>
</template>

<script>
import Modal from "@/resources/components/Modal";
import statusOrder from "src/util/valueObjects/statusOrder";
import moment from "moment";
import axios from "axios";
import * as Cookies from "js-cookie";

export default {
  name: "SearchNfModal",
  components: {
    Modal,
  },
  data() {
    return {
      isModalVisible: false,
      isDarkMode: null,
      nfNumber: null,
      isLoading: false,
      base: document.location.protocol + '//' + process.env.VUE_APP_API_HOST,
      nfData: null,
      statusItems: [],
    }
  },
  computed: {
    token() {
      return Cookies.getJSON('token');
    },
  },
  methods: {
    show(nfNumber) {
      this.$set(this, 'nfNumber', nfNumber);
      this.search();

      this.$set(this, 'isDarkMode', localStorage.getItem('dark_mode'));
      this.$set(this, 'isModalVisible', true);
    },
    async search() {
      this.$set(this, 'isLoading', true);

      const headers = {'Authorization': `Bearer ${this.token.access_token}`};
      await axios.get(this.base + '/api/orders/search/nf/' + this.nfNumber, {headers: headers})
        .then(response => {
          this.$set(this, 'nfData', response.data.data);
          this.setStatusItems();
        })
    },
    setStatusItems() {
      this.statusItems = [
        {
          id: 1,
          label: 'request_created',
          timestamp: this.nfData.request_created_date_time,
          color: this.nfData.request_created_date_time === null ? 'default' : statusOrder.CREATED,
          icon: this.nfData.request_created_date_time === null ? 'tim-icons icon-simple-delete' : 'tim-icons icon-check-2',
        },
        {
          id: statusOrder.COLLECTED,
          label: 'collected',
          timestamp: this.nfData.collected_date_time,
          color: this.nfData.collected_date_time === null ? 'default' : statusOrder.COLLECTED,
          icon: this.nfData.collected_date_time === null ? 'tim-icons icon-simple-delete' : 'tim-icons icon-check-2',
        },
        {
          id: statusOrder.ON_ROUTE,
          label: 'on_route',
          timestamp: this.nfData.on_route_date_time,
          color: this.nfData.on_route_date_time === null ? 'default' : statusOrder.ON_ROUTE,
          icon: this.nfData.on_route_date_time === null ? 'tim-icons icon-simple-delete' : 'tim-icons icon-check-2',
        },
        {
          id: statusOrder.DELIVERED,
          label: 'delivered',
          timestamp: this.nfData.delivered_date_time,
          color: this.nfData.delivered_date_time === null ? 'default' : statusOrder.DELIVERED,
          icon: this.nfData.delivered_date_time === null ? 'tim-icons icon-simple-delete' : 'tim-icons icon-check-2',
        },
      ];

      this.$set(this, 'isLoading', false);
    },
    async hide() {
      this.$set(this, 'nfNumber', null);
      this.$set(this, 'nfData', null);
      this.$set(this, 'statusItems', []);
      this.$set(this, 'isLoading', false);
      this.$set(this, 'isModalVisible', false);

      this.$emit('close');
    },
    modalColor() {
      if (this.isDarkMode == 'true')
        return 'modal-black';
      return 'modal-white';
    },
    formatDate(date) {
      return date ? moment(date).format('DD/MM/Y HH:mm') : 'Não informado'
    },
    redirectToOrder(orderId) {
      this.$router.push({
        name: `${this.token.user.role.name}.order_update`,
        params: {id: orderId}
      });

      this.hide();
    },
  }
}

</script>
<style scoped>
.custom-spinner {
  margin: 30px auto;
  border: 6px solid #f3f3f3;
  border-top: 6px solid #FF854D;
  border-radius: 50%;
  width: 48px;
  height: 48px;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>